import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";

import { COLORS } from "@/constants/colors";
import { DatasetState } from "@/store/slice/datasetSlice";
import { SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  Dataset,
  DatasetConnectorItem,
  DatasetData,
  DatasetDataType,
  DatasetExploreItem,
  DatasetMetadata,
  DatasetStatsCountStatusENUM,
  EColorLabel,
  EColorNames,
  EDatasetConnector,
  EDatasetConnectorStatus,
  GetDatasetListResponse,
  SpatialAggregationTypeItem
} from "@/types/dataset";
import { EOutputAnalysisType } from "@/types/spatial";
import { createMockDatasetDataColumn } from "@/utils/test/factories/dataset";

export const allDatasets: DatasetExploreItem[] = [
  {
    id: "1db1e893-f6a0-4331-919a-20fd9901d71c",
    name: "Population by Province",
    country_id: "id",
    source: "bvt",
    spatial_aggregation_type: "polygon",
    short_description: "Population distribution across provinces in Indonesia",
    published: true,
    created_at: "2025-01-30T09:28:23.798Z",
    updated_at: "2025-01-30T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "879e0389-b0da-4079-b223-4221e00df82e",
    name: "ATM Locations",
    country_id: "my",
    source: "bvt",
    spatial_aggregation_type: "poi",
    short_description: "ATM point locations in Malaysia major cities",
    published: true,
    created_at: "2025-01-30T17:38:33.71Z",
    updated_at: "2025-01-30T17:38:33.71Z",
    total_rows: 100
  },
  {
    id: "056888c6-3bc2-419e-b8ae-9a5385c2ad6b",
    name: "Administrative Boundaries",
    country_id: "vn",
    source: "user",
    spatial_aggregation_type: "adm_area",
    short_description: "Administrative area boundaries in Vietnam",
    published: true,
    created_at: "2025-02-13T04:47:26.79458Z",
    updated_at: "2025-02-13T04:47:26.79458Z",
    total_rows: 100
  },
  {
    id: "8860c660-f045-49cc-95c3-e843d173c4c5",
    name: "Shopping Centers",
    country_id: "id",
    source: "user",
    spatial_aggregation_type: "poi",
    short_description: "Shopping center locations in Jakarta",
    published: true,
    created_at: "2025-02-13T06:39:59.338764Z",
    updated_at: "2025-02-13T06:39:59.338764Z",
    total_rows: 100
  },
  {
    id: "127b53e7-1a60-446e-8585-a3d80ecddf99",
    name: "School Districts H3",
    country_id: "my",
    source: "bvt",
    spatial_aggregation_type: "adm_area",
    short_description: "School district boundaries in Kuala Lumpur",
    published: true,
    created_at: "2025-02-13T04:24:50.000006Z",
    updated_at: "2025-02-13T04:24:50.000006Z",
    total_rows: 100
  },
  {
    id: "3d365b0e-b6d9-4130-b5d4-2fe8214f18d6",
    name: "Business Districts Grid",
    country_id: "vn",
    source: "user",
    spatial_aggregation_type: "adm_area",
    short_description: "Combined business district data in Ho Chi Minh City",
    published: true,
    created_at: "2025-02-13T04:47:21.148257Z",
    updated_at: "2025-02-13T04:47:21.148257Z",
    total_rows: 100
  },
  {
    id: "97f386af-701d-4f93-8729-36c98a787de7",
    name: "Demographic Female Married Age Group Prefecture Japan 2020",
    country_id: "id",
    source: "user",
    spatial_aggregation_type: "polygon",
    short_description:
      "Data Demographic Female Married Age Group by Prefecture in Japan 2020",
    published: true,
    created_at: "2025-02-13T07:23:16.058249Z",
    updated_at: "2025-02-13T07:23:16.058249Z",
    total_rows: 100
  }
];

// Helper function to get paginated response
const getPaginatedResponse = (
  page: number,
  perPage: number = 2
): GetDatasetListResponse => {
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const totalItems = allDatasets.length;
  const totalPages = Math.ceil(totalItems / perPage);

  return {
    data: allDatasets.slice(startIndex, endIndex),
    meta: {
      pagination: {
        next: page < totalPages ? page + 1 : 0,
        prev: page > 1 ? page - 1 : 0,
        per_page: perPage,
        total: totalItems,
        total_pages: totalPages
      }
    }
  };
};

// Export paginated responses
export const mockDatasetListResponse = getPaginatedResponse(1);
export const mockDatasetListResponsePage1 = getPaginatedResponse(1);
export const mockDatasetListResponsePage2 = getPaginatedResponse(2);
export const mockDatasetListResponsePage3 = getPaginatedResponse(3);
export const mockDatasetListResponsePage4 = getPaginatedResponse(4);

// Dataset sample data matching the image
export const sampleDatasetManagement: DatasetExploreItem[] = [
  {
    id: "1",
    name: "Retail and Commercial POI Distribution",
    short_description: "Point-based dataset of stores, malls, and businesses",
    country_id: "id",
    source: "bvt",
    spatial_aggregation_type: "poi",
    published: true,
    created_at: "2025-01-30T09:28:23.798Z",
    updated_at: "2025-01-30T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "2",
    name: "Customer Demographics",
    short_description:
      "Aggregated census and income data by districts, cities, or regions",
    country_id: "id",
    source: "bvt",
    spatial_aggregation_type: "polygon",
    published: true,
    created_at: "2025-01-29T09:28:23.798Z",
    updated_at: "2025-01-29T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "3",
    name: "Competitor Locations and Market Saturation Zones",
    short_description:
      "Point and polygon data showing competitor presence and influence areas",
    country_id: "vn",
    source: "bvt",
    spatial_aggregation_type: "adm_area",
    published: true,
    created_at: "2025-01-28T09:28:23.798Z",
    updated_at: "2025-01-28T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "4",
    name: "High-Traffic POIs and Pedestrian Flow Analysis",
    short_description:
      "Point clusters of busy locations such as malls, transit hubs, and tourist attractions",
    country_id: "id",
    source: "bvt",
    spatial_aggregation_type: "poi",
    published: false,
    created_at: "2025-01-27T09:28:23.798Z",
    updated_at: "2025-01-27T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "5",
    name: "Billboard and OOH Advertising Effectiveness by Coverage Area",
    short_description:
      "Polygon dataset of ad reach zones based on visibility and traffic counts",
    country_id: "jp",
    source: "bvt",
    spatial_aggregation_type: "adm_area",
    published: false,
    created_at: "2025-01-26T09:28:23.798Z",
    updated_at: "2025-01-26T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "6",
    name: "E-Commerce Order Density by Zip Code",
    short_description:
      "Choropleth-style polygon data of online shopping activity per administrative unit",
    country_id: "id",
    source: "bvt",
    spatial_aggregation_type: "adm_area",
    published: true,
    created_at: "2025-01-24T09:28:23.798Z",
    updated_at: "2025-01-24T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "7",
    name: "Customer Footfall Heatmap by Shopping Districts",
    short_description:
      "Spatial clustering of movement data in retail-heavy areas",
    country_id: "vn",
    source: "bvt",
    spatial_aggregation_type: "polygon",
    published: true,
    created_at: "2025-01-22T09:28:23.798Z",
    updated_at: "2025-01-22T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "8",
    name: "Purchase Behavior by Land Use Type",
    short_description:
      "Polygon-based segmentation of transactions in residential, commercial, and mixed-use zones",
    country_id: "id",
    source: "bvt",
    spatial_aggregation_type: "adm_area",
    published: false,
    created_at: "2025-01-20T09:28:23.798Z",
    updated_at: "2025-01-20T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "9",
    name: "Store Expansion Suitability Analysis by Administrative Boundaries",
    short_description:
      "Spatial aggregation of population, spending power, and competition within city zones",
    country_id: "id",
    source: "bvt",
    spatial_aggregation_type: "polygon",
    published: true,
    created_at: "2025-01-15T09:28:23.798Z",
    updated_at: "2025-01-15T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "10",
    name: "Consumer Spending Clusters by Commercial Zones",
    short_description:
      "Polygon-based analysis of transaction volume in business districts, malls, and retail centers",
    country_id: "vn",
    source: "bvt",
    spatial_aggregation_type: "polygon",
    published: false,
    created_at: "2025-01-10T09:28:23.798Z",
    updated_at: "2025-01-10T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "11",
    name: "Brand Affinity and Store Visits by Neighborhood",
    short_description:
      "Aggregated location intelligence on customer preferences within city subdivisions or postal codes",
    country_id: "jp",
    source: "bvt",
    spatial_aggregation_type: "adm_area",
    published: false,
    created_at: "2025-01-07T09:28:23.798Z",
    updated_at: "2025-01-07T09:34:27.861Z",
    total_rows: 100
  },
  {
    id: "12",
    name: "Loyalty Program User Concentration Around POIs",
    short_description:
      "Point-based dataset showing loyalty members' proximity to business locations",
    country_id: "id",
    source: "bvt",
    spatial_aggregation_type: "poi",
    published: true,
    created_at: "2024-12-30T09:28:23.798Z",
    updated_at: "2024-12-30T09:34:27.861Z",
    total_rows: 100
  }
];

// Helper function to get paginated response for sample dataset management
const getPaginatedSampleDatasetResponse = (
  page: number,
  perPage: number = 5
): {
  data: DatasetExploreItem[];
  meta: {
    pagination: {
      next: number;
      prev: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  };
} => {
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const totalItems = sampleDatasetManagement.length;
  const totalPages = Math.ceil(totalItems / perPage);

  return {
    data: sampleDatasetManagement.slice(startIndex, endIndex),
    meta: {
      pagination: {
        next: page < totalPages ? page + 1 : 0,
        prev: page > 1 ? page - 1 : 0,
        per_page: perPage,
        total: totalItems,
        total_pages: totalPages
      }
    }
  };
};

// Export paginated responses for sample dataset management
export const mockSampleDatasetResponsePage1 =
  getPaginatedSampleDatasetResponse(1);
export const mockSampleDatasetResponsePage2 =
  getPaginatedSampleDatasetResponse(2);
export const mockSampleDatasetResponsePage3 =
  getPaginatedSampleDatasetResponse(3);

export const datasetMockData: DatasetState["datasets"] = {
  "profiling-point-test": {
    data: {
      id: "profiling-point-test",
      name: "Profiling Point Test",
      display_name: "Profiling Point Test",
      spatial_aggregation_type: EOutputAnalysisType.PROFILING,
      country_id: "id",
      source: "bvt",
      short_description: "short description",
      published: true,
      created_at: "2021-01-01",
      updated_at: "2021-01-01",
      available: true,
      total_rows: 1,
      schema: []
    },
    properties: {
      isVisible: true,
      spatialType: "poi"
    },
    selectedCategory: {
      attribute_name: "name",
      created_at: "",
      data_type: DataSchemaType.TEXT,
      id: "1",
      is_nullable: false,
      is_spatial: false,
      position: 1,
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    style: {
      stroke: {
        attribute_id: "1",
        code: "Name",
        attribute_name: "Name",
        opacity: 1,
        width: 1
      },
      color: "#fff",
      colorFamily: EColorNames.TEAL,
      colorRanges: [],
      fillColor: "#088C7A",
      fillOpacity: 0.7,
      opacity: 1,
      visibility: "visible",
      weight: 1,
      labelConfig: {
        attribute_id: "1",
        code: "Name",
        attribute_name: "Name",
        show_label: true,
        color_scale: EColorLabel.BLACK_WHITE,
        opacity: 0.7
      },
      colorSteps: 10
    },
    categories: [
      {
        attribute_name: "id",
        created_at: "",
        data_type: DataSchemaType.INT,
        id: "0",
        is_nullable: false,
        is_spatial: false,
        position: 0,
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        attribute_name: "name",
        created_at: "",
        data_type: DataSchemaType.TEXT,
        id: "1",
        is_nullable: false,
        is_spatial: false,
        position: 1,
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        attribute_name: "point_geom",
        created_at: "",
        data_type: DataSchemaType.GEOMETRY,
        id: "2",
        is_nullable: false,
        is_spatial: false,
        position: 2,
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      }
    ]
  }
};

export const historyMockData: DatasetState["history"] = {
  visualizationOrder: ["profiling-point-test"],
  datasets: datasetMockData,
  rawDatasets: {}
};

export const selectedDataset: Dataset = {
  data: {
    id: "2170a6ad-731f-4665-bd3d-6fcd33d95ef6",
    name: "spatial analysis result",
    display_name: "spatial analysis result",
    spatial_aggregation_type: EOutputAnalysisType.PROFILING,
    country_id: "id",
    source: "bvt",
    short_description: "short description",
    published: true,
    created_at: "2021-01-01",
    updated_at: "2021-01-01",
    available: true,
    total_rows: 9,
    schema: []
  },
  properties: {
    isVisible: true,
    spatialType: EOutputAnalysisType.PROFILING
  },
  style: {
    stroke: {
      attribute_id: "1",
      code: "Name",
      attribute_name: "Name",
      opacity: 1,
      width: 1
    },
    weight: 1,
    opacity: 1,
    color: "#fff",
    fillColor: "#088C7A",
    colorFamily: EColorNames.TEAL,
    fillOpacity: 0.7,
    visibility: "visible",
    colorRanges: [
      {
        value: "A1",
        color: "#033B33",
        label: "A1"
      },
      {
        value: "A2",
        color: "#044D43",
        label: "A2"
      },
      {
        value: "A3",
        color: "#066357",
        label: "A3"
      }
    ],
    labelConfig: {
      attribute_id: "1",
      code: "Name",
      attribute_name: "Name",
      show_label: true,
      color_scale: EColorLabel.BLACK_WHITE,
      opacity: 0.7
    },
    colorSteps: 10
  },
  categories: [
    {
      id: "0",
      code: "catchment_id",
      attribute_name: "catchment_id",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 0,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "1",
      code: "catchment_geometry",
      attribute_name: "catchment_geometry",
      data_type: DataSchemaType.GEOMETRY,
      is_nullable: false,
      is_spatial: false,
      position: 1,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "2",
      code: "location_id",
      attribute_name: "location_id",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 2,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "3",
      code: "location_geometry",
      attribute_name: "location_geometry",
      data_type: DataSchemaType.GEOMETRY,
      is_nullable: false,
      is_spatial: false,
      position: 3,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "4",
      code: "location_name",
      attribute_name: "location_name",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 4,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "5",
      code: "score",
      attribute_name: "score",
      data_type: DataSchemaType.FLOAT,
      is_nullable: false,
      is_spatial: false,
      position: 5,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "6",
      code: "penduduk_proportion",
      attribute_name: "penduduk_proportion",
      data_type: DataSchemaType.FLOAT,
      is_nullable: false,
      is_spatial: false,
      position: 6,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    }
  ],
  selectedCategory: {
    id: "2",
    code: "location_id",
    attribute_name: "location_id",
    data_type: DataSchemaType.TEXT,
    is_nullable: false,
    is_spatial: false,
    position: 2,
    created_at: "",
    updated_at: "",
    is_reference_geo: false,
    is_reference_id: false,
    is_visible: true,
    paired_key_attribute_id: null
  }
};

export const datasets: {
  [id: string]: Dataset;
} = {
  "2170a6ad-731f-4665-bd3d-6fcd33d95ef6": {
    data: {
      id: "2170a6ad-731f-4665-bd3d-6fcd33d95ef6",
      name: "spatial analysis result",
      spatial_aggregation_type: EOutputAnalysisType.GRID,
      country_id: "id",
      source: "bvt",
      short_description: "short description",
      published: true,
      created_at: "2021-01-01",
      updated_at: "2021-01-01",
      available: true,
      total_rows: 9,
      schema: []
    },
    properties: {
      isVisible: true,
      spatialType: EOutputAnalysisType.GRID
    },
    style: {
      stroke: {
        attribute_id: "1",
        code: "Name",
        attribute_name: "Name",
        opacity: 1,
        width: 1
      },
      weight: 1,
      opacity: 1,
      color: "#fff",
      fillColor: "#088C7A",
      colorFamily: EColorNames.TEAL,
      fillOpacity: 0.7,
      visibility: "visible",
      colorRanges: [
        {
          value: "A1",
          color: "#033B33",
          label: "A1"
        },
        {
          value: "A2",
          color: "#044D43",
          label: "A2"
        },
        {
          value: "A3",
          color: "#066357",
          label: "A3"
        }
      ],
      labelConfig: {
        attribute_id: "1",
        code: "Name",
        attribute_name: "Name",
        show_label: true,
        color_scale: EColorLabel.BLACK_WHITE,
        opacity: 0.7
      },
      colorSteps: 10
    },
    categories: [
      {
        id: "0",
        code: "catchment_id",
        attribute_name: "catchment_id",
        data_type: DataSchemaType.TEXT,
        is_nullable: false,
        is_spatial: false,
        position: 0,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        id: "1",
        code: "catchment_geometry",
        attribute_name: "catchment_geometry",
        data_type: DataSchemaType.GEOMETRY,
        is_nullable: false,
        is_spatial: false,
        position: 1,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        id: "2",
        code: "location_id",
        attribute_name: "location_id",
        data_type: DataSchemaType.TEXT,
        is_nullable: false,
        is_spatial: false,
        position: 2,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        id: "3",
        code: "location_geometry",
        attribute_name: "location_geometry",
        data_type: DataSchemaType.GEOMETRY,
        is_nullable: false,
        is_spatial: false,
        position: 3,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        id: "4",
        code: "location_name",
        attribute_name: "location_name",
        data_type: DataSchemaType.TEXT,
        is_nullable: false,
        is_spatial: false,
        position: 4,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        id: "5",
        code: "score",
        attribute_name: "score",
        data_type: DataSchemaType.FLOAT,
        is_nullable: false,
        is_spatial: false,
        position: 5,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        id: "6",
        code: "penduduk_proportion",
        attribute_name: "penduduk_proportion",
        data_type: DataSchemaType.FLOAT,
        is_nullable: false,
        is_spatial: false,
        position: 6,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      }
    ],
    selectedCategory: {
      id: "2",
      code: "location_id",
      attribute_name: "location_id",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 2,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    }
  }
};

export const parsedData = [
  {
    catchment_id: "1",
    catchment_geometry: "POLYGON ()",
    location_id: "A1",
    location_geometry: "POINT ()",
    location_name: "marker-0",
    score: 661739.3242605974,
    penduduk_proportion: 661739.3242605974
  },
  {
    catchment_id: "2",
    catchment_geometry: "POLYGON ()",
    location_id: "A1",
    location_geometry: "POINT ()",
    location_name: "marker-0",
    score: 652027.9650644484,
    penduduk_proportion: 652027.9650644484
  },
  {
    catchment_id: "3",
    catchment_geometry: "POLYGON ()",
    location_id: "A1",
    location_geometry: "POINT ()",
    location_name: "marker-0",
    score: 759019.0585670826,
    penduduk_proportion: 759019.0585670826
  },
  {
    catchment_id: "4",
    catchment_geometry: "POLYGON ()",
    location_id: "A2",
    location_geometry: "POINT ()",
    location_name: "marker-1",
    score: 661739.3242605974,
    penduduk_proportion: 661739.3242605974
  },
  {
    catchment_id: "5",
    catchment_geometry: "POLYGON ()",
    location_id: "A2",
    location_geometry: "POINT ()",
    location_name: "marker-1",
    score: 652027.9650644484,
    penduduk_proportion: 652027.9650644484
  },
  {
    catchment_id: "6",
    catchment_geometry: "POLYGON ()",
    location_id: "A2",
    location_geometry: "POINT ()",
    location_name: "marker-1",
    score: 759019.0585670826,
    penduduk_proportion: 759019.0585670826
  },
  {
    catchment_id: "7",
    catchment_geometry: "POLYGON ()",
    location_id: "A3",
    location_geometry: "POINT ()",
    location_name: "marker-2",
    score: 661739.3242605974,
    penduduk_proportion: 661739.3242605974
  },
  {
    catchment_id: "8",
    catchment_geometry: "POLYGON ()",
    location_id: "A3",
    location_geometry: "POINT ()",
    location_name: "marker-2",
    score: 652027.9650644484,
    penduduk_proportion: 652027.9650644484
  },
  {
    catchment_id: "9",
    catchment_geometry: "POLYGON ()",
    location_id: "A3",
    location_geometry: "POINT ()",
    location_name: "marker-2",
    score: 759019.0585670826,
    penduduk_proportion: 759019.0585670826
  }
];

export const datasetMetadata: DatasetMetadata = {
  country_id: "id",
  created_at: "2025-05-09T08:09:24.132644Z",
  id: "f043eabd-0938-44c2-8023-78a164a705e3",
  name: "10 Tourist Destinations",
  published: false,
  schema: [
    {
      attribute_name: "No",
      created_at: "2025-05-09T08:09:24.140761Z",
      data_type: DataSchemaType.INT,
      id: "1fb41f99-6f22-4e46-b69a-c25c797e3dd3",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: false,
      is_spatial: false,
      position: 1,
      updated_at: "2025-05-09T08:09:24.140761Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "Nama Tempat",
      created_at: "2025-05-09T08:09:24.144719Z",
      data_type: DataSchemaType.TEXT,
      id: "e250e208-4887-44ad-957b-c4eef0eef217",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: true,
      is_spatial: false,
      position: 2,
      updated_at: "2025-05-09T08:14:26.226518Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "Kategori",
      created_at: "2025-05-09T08:09:24.149015Z",
      data_type: DataSchemaType.TEXT,
      id: "1de35118-4806-470e-9f42-9d55a65e9410",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: false,
      is_spatial: false,
      position: 3,
      updated_at: "2025-05-09T08:09:24.149015Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "Code",
      created_at: "2025-05-09T08:09:24.15272Z",
      data_type: DataSchemaType.TEXT,
      id: "34fed9b4-a307-43b1-91b3-21ff773ba9a6",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: false,
      is_spatial: false,
      position: 4,
      updated_at: "2025-05-09T08:09:24.15272Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "adm_area_id",
      created_at: "2025-05-09T08:13:36.003384Z",
      data_type: DataSchemaType.TEXT,
      id: "5c28de07-e634-477e-9994-739378b2bc1e",
      is_nullable: true,
      is_reference_geo: true,
      is_reference_id: false,
      is_spatial: true,
      position: 0,
      updated_at: "2025-05-09T08:13:36.003384Z",
      is_visible: true,
      paired_key_attribute_id: null
    }
  ],
  short_description: "A",
  source: "user",
  spatial_aggregation_type: "poi" as SpatialAggregationType,
  total_rows: 10,
  updated_at: "2025-05-09T08:13:36.86903Z"
};

export const translateDatasetMetadata = {
  country_id: "",
  created_at: "2025-05-13T06:29:40.21701Z",
  id: "2dc31dfc-06c5-4d89-be1e-29b3cb99c8c0",
  name: "Sample Dataset With Lat Long_20R",
  published: false,
  schema: [
    {
      attribute_name: "Name",
      created_at: "2025-05-13T06:29:40.227124Z",
      data_type: "string",
      id: "c77b2f5e-f342-4535-ba88-a86fb1b7f880",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: true,
      is_spatial: false,
      position: 1,
      updated_at: "2025-05-13T06:29:40.227124Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "Population",
      created_at: "2025-05-13T06:29:40.232139Z",
      data_type: "int",
      id: "15ef9313-d512-4160-808a-1ed63eff4d28",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: false,
      is_spatial: false,
      position: 2,
      updated_at: "2025-05-13T06:29:40.232139Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "Latitude",
      created_at: "2025-05-13T06:29:40.237423Z",
      data_type: "float",
      id: "eed42f4d-3752-450c-b2c0-cb5b6fa92650",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: false,
      is_spatial: false,
      position: 3,
      updated_at: "2025-05-13T06:29:40.237423Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "Longitude",
      created_at: "2025-05-13T06:29:40.242857Z",
      data_type: "float",
      id: "bc52324b-f039-475e-8323-d684df2e6321",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: false,
      is_spatial: false,
      position: 4,
      updated_at: "2025-05-13T06:29:40.242857Z",
      is_visible: true,
      paired_key_attribute_id: null
    }
  ],
  short_description: "",
  source: "user",
  spatial_aggregation_type: "",
  total_rows: 20,
  updated_at: "2025-05-13T06:29:40.21701Z"
};

export const translateDatasetData = [
  {
    Name: "GGSP Aip",
    Population: 100,
    Latitude: -6.2743121,
    Longitude: 106.8437393
  },
  {
    Name: "GGSP Albana 212",
    Population: 500,
    Latitude: -6.2599574,
    Longitude: 106.785617
  },
  {
    Name: "GGSP Amel",
    Population: 350,
    Latitude: -6.3335271,
    Longitude: 106.8334284
  },
  {
    Name: "GGSP Araya",
    Population: 1250,
    Latitude: -6.1126292,
    Longitude: 106.8842192
  },
  {
    Name: "GGSP Asih",
    Population: 550,
    Latitude: -6.3197422,
    Longitude: 106.8713221
  },
  {
    Name: "He-Mart",
    Population: 553,
    Latitude: -6.3401242,
    Longitude: 106.9025006
  },
  {
    Name: "Idol Mart Cibubur",
    Population: 104,
    Latitude: -6.3466864,
    Longitude: 106.8730219
  },
  {
    Name: "King Mart",
    Population: 504,
    Latitude: -6.313197,
    Longitude: 106.8037044
  },
  {
    Name: "Makmur Shop Lebak Bulus",
    Population: 354,
    Latitude: -6.2935572,
    Longitude: 106.7823666
  },
  {
    Name: "Mala Mart",
    Population: 1254,
    Latitude: -6.1339062,
    Longitude: 106.8576391
  },
  {
    Name: "Mitra Bukalapak Aceng JY",
    Population: 355,
    Latitude: -6.1192185,
    Longitude: 106.8878284
  },
  {
    Name: "Mitra Bukalapak Ade",
    Population: 1255,
    Latitude: -6.2687617,
    Longitude: 106.8239949
  },
  {
    Name: "Mitra Bukalapak Adelia Cell",
    Population: 555,
    Latitude: -6.310439,
    Longitude: 106.836344
  },
  {
    Name: "Mitra Bukalapak Ude Jaya",
    Population: 1273,
    Latitude: -6.343783,
    Longitude: 106.8152277
  },
  {
    Name: "Mitra Bukalapak Usman Cell",
    Population: 573,
    Latitude: -6.2964551,
    Longitude: 106.8314784
  },
  {
    Name: "Mitra Bukalapak Wahid",
    Population: 124,
    Latitude: -6.2630027,
    Longitude: 106.8004345
  },
  {
    Name: "Mitra Bukalapak Warung Alika",
    Population: 524,
    Latitude: -6.305268,
    Longitude: 106.828427
  },
  {
    Name: "Mitra Bukalapak Wati",
    Population: 374,
    Latitude: -6.3356877,
    Longitude: 106.807015
  },
  {
    Name: "Mitra Bukalapak Wedy",
    Population: 1274,
    Latitude: -6.1636328,
    Longitude: 106.8449744
  },
  {
    Name: "Mitra Bukalapak Windy",
    Population: 574,
    Latitude: -6.1773957,
    Longitude: 106.8520986
  },
  {
    Name: "",
    Population: null,
    Latitude: null,
    Longitude: null
  }
];

export const gridFeatureData: {
  [datasetId: string]: FeatureCollection<Geometry, GeoJsonProperties>;
} = {
  "2170a6ad-731f-4665-bd3d-6fcd33d95ef6": {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        id: "1-0",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [115.98198720613576, 0.01782942367296269],
              [116.09812534479363, -0.5827354103262796],
              [116.654074761912, -0.8075697489805813],
              [117.1002240297898, -0.4094070515196965],
              [116.98651404212949, 0.21536227424505627],
              [116.42729936847816, 0.43749299068406705],
              [116.20578079634771, 0.2371660978013493],
              [115.98198720613576, 0.01782942367296269]
            ]
          ]
        },
        properties: {
          grid_id: "83688efffffffff",
          score: 0,
          penduduk_proportion: 0,
          ["Geometry Source"]: "grid_geometry"
        }
      },
      {
        type: "Feature",
        id: "1-0",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [112.64324200737462, 0.40260897075362007],
              [112.51513195489548, 1.0196927820551427],
              [111.92217778962521, 1.190728835245759],
              [111.45720274503351, 0.7406892204716543],
              [111.58862218500414, 0.12160340843554658],
              [112.18171712691155, -0.04545965482113946],
              [112.64324200737462, 0.40260897075362007]
            ]
          ]
        },
        properties: {
          grid_id: "8368b0fffffffff",
          score: 0,
          penduduk_proportion: 0,
          ["Geometry Source"]: "grid_geometry"
        }
      },
      {
        type: "Feature",
        id: "1-0",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [113.72787454422898, -2.189789649641762],
              [113.60438093799625, -1.5886847325451379],
              [113.0235829573058, -1.429423795528788],
              [112.56600813761236, -1.8754162808777286],
              [112.69278258356637, -2.4784335545924363],
              [113.2738585823367, -2.6335662975021146],
              [113.72787454422898, -2.189789649641762]
            ]
          ]
        },
        properties: {
          grid_id: "836890fffffffff",
          score: 209521.35718697385,
          penduduk_proportion: 209521.35718697385,
          ["Geometry Source"]: "grid_geometry"
        }
      }
    ]
  }
};

export const gridDatasets: {
  [id: string]: Dataset;
} = {
  "2170a6ad-731f-4665-bd3d-6fcd33d95ef6": {
    data: {
      id: "2170a6ad-731f-4665-bd3d-6fcd33d95ef6",
      name: "spatial analysis result",
      spatial_aggregation_type: EOutputAnalysisType.GRID,
      country_id: "id",
      source: "bvt",
      short_description: "short description",
      published: true,
      created_at: "2021-01-01",
      updated_at: "2021-01-01",
      available: true,
      total_rows: 3,
      schema: []
    },
    properties: {
      isVisible: true,
      spatialType: EOutputAnalysisType.GRID
    },
    style: {
      stroke: {
        attribute_id: "1",
        code: "Name",
        attribute_name: "Name",
        opacity: 1,
        width: 1
      },
      weight: 1,
      opacity: 1,
      color: "#fff",
      fillColor: "#088C7A",
      colorFamily: EColorNames.TEAL,
      fillOpacity: 0.7,
      visibility: "visible",
      colorRanges: [
        {
          min: 0,
          max: 0,
          color: "#033B33",
          label: "0"
        },
        {
          min: 209521.35718697385,
          max: 209521.35718697385,
          color: "#044D43",
          label: "209521.357"
        }
      ],
      labelConfig: {
        attribute_id: "1",
        code: "Name",
        attribute_name: "Name",
        show_label: true,
        color_scale: EColorLabel.BLACK_WHITE,
        opacity: 0.7
      },
      colorSteps: 10
    },
    categories: [
      {
        id: "0",
        code: "grid_id",
        attribute_name: "grid_id",
        data_type: DataSchemaType.TEXT,
        is_nullable: false,
        is_spatial: false,
        position: 0,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        id: "1",
        code: "grid_geometry",
        attribute_name: "grid_geometry",
        data_type: DataSchemaType.GEOMETRY,
        is_nullable: false,
        is_spatial: false,
        position: 1,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        id: "2",
        code: "score",
        attribute_name: "score",
        data_type: DataSchemaType.FLOAT,
        is_nullable: false,
        is_spatial: false,
        position: 2,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        id: "3",
        code: "penduduk_proportion",
        attribute_name: "penduduk_proportion",
        data_type: DataSchemaType.FLOAT,
        is_nullable: false,
        is_spatial: false,
        position: 3,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      }
    ],
    selectedCategory: {
      id: "2",
      code: "score",
      attribute_name: "score",
      data_type: DataSchemaType.FLOAT,
      is_nullable: false,
      is_spatial: false,
      position: 2,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    }
  }
};

export const parsedGridData = [
  {
    grid_id: "83688efffffffff",
    grid_geometry:
      "POLYGON ((115.98198720613576 0.01782942367296269, 116.09812534479363 -0.5827354103262796, 116.654074761912 -0.8075697489805813, 117.1002240297898 -0.4094070515196965, 116.98651404212949 0.21536227424505627, 116.42729936847816 0.43749299068406705, 116.20578079634771 0.2371660978013493, 115.98198720613576 0.01782942367296269))",
    score: 0,
    penduduk_proportion: 0
  },
  {
    grid_id: "8368b0fffffffff",
    grid_geometry:
      "POLYGON ((112.64324200737462 0.40260897075362007, 112.51513195489548 1.0196927820551427, 111.92217778962521 1.190728835245759, 111.45720274503351 0.7406892204716543, 111.58862218500414 0.12160340843554658, 112.18171712691155 -0.04545965482113946, 112.64324200737462 0.40260897075362007))",
    score: 0,
    penduduk_proportion: 0
  },
  {
    grid_id: "836890fffffffff",
    grid_geometry:
      "POLYGON ((113.72787454422898 -2.189789649641762, 113.60438093799625 -1.5886847325451379, 113.0235829573058 -1.429423795528788, 112.56600813761236 -1.8754162808777286, 112.69278258356637 -2.4784335545924363, 113.2738585823367 -2.6335662975021146, 113.72787454422898 -2.189789649641762))",
    score: 209521.35718697385,
    penduduk_proportion: 209521.35718697385
  }
];

export const populationByProvinceDataset = {
  data: {
    country_id: "id",
    created_at: "2025-01-30T09:28:23.798Z",
    id: "1db1e893-f6a0-4331-919a-20fd9901d71c_1",
    name: "population by province",
    published: true,
    schema: [
      {
        attribute_name: "id",
        created_at: "2025-01-30T09:28:23.831Z",
        data_type: DataSchemaType.INT,
        id: "21735252-9b5b-42c8-b244-9e39ceec5f1e",
        is_nullable: false,
        is_reference_geo: false,
        is_reference_id: false,
        is_spatial: false,
        position: 1,
        updated_at: "2025-01-30T09:28:23.831Z",
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        attribute_name: "nama",
        created_at: "2025-01-30T09:28:23.832Z",
        data_type: DataSchemaType.TEXT,
        id: "9ff554cf-0b90-4c98-805a-e80d27387d2a",
        is_nullable: false,
        is_reference_geo: false,
        is_reference_id: false,
        is_spatial: false,
        position: 2,
        updated_at: "2025-01-30T09:28:23.832Z",
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        attribute_name: "ibukota",
        created_at: "2025-01-30T09:28:23.83Z",
        data_type: DataSchemaType.TEXT,
        id: "f1145628-38e8-4f9a-b507-e1524769b136",
        is_nullable: false,
        is_reference_geo: false,
        is_reference_id: true,
        is_spatial: false,
        position: 3,
        updated_at: "2025-04-21T09:42:00.437679Z",
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        attribute_name: "penduduk",
        created_at: "2025-01-30T09:38:44.381Z",
        data_type: DataSchemaType.INT,
        id: "a5e850bd-08fa-4043-9a2a-29cc073d1da4",
        is_nullable: false,
        is_reference_geo: false,
        is_reference_id: false,
        is_spatial: false,
        position: 7,
        updated_at: "2025-01-30T09:38:44.381Z",
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        attribute_name: "geom",
        created_at: "2025-01-30T09:38:44.381Z",
        data_type: DataSchemaType.GEOMETRY,
        id: "2d6c2592-2c3d-4e64-b35b-2b565c080981",
        is_nullable: false,
        is_reference_geo: true,
        is_reference_id: false,
        is_spatial: true,
        position: 8,
        updated_at: "2025-04-21T03:03:35.471752Z",
        is_visible: true,
        paired_key_attribute_id: null
      }
    ],
    short_description: "population province in indonesia",
    source: "bvt",
    spatial_aggregation_type: "polygon",
    total_rows: 0,
    updated_at: "2025-05-06T01:15:06.810837Z",
    available: true
  },
  properties: {
    isVisible: true,
    spatialType: "polygon"
  },
  style: {
    weight: 1,
    opacity: 0.7,
    color: "#fff",
    fillColor: "#088C7A",
    colorFamily: "TEAL",
    fillOpacity: 0.7,
    visibility: "visible",
    colorRanges: [
      {
        value: "ACEH",
        color: "#033B33",
        label: "ACEH"
      },
      {
        value: "SUMATERA UTARA",
        color: "#044D43",
        label: "SUMATERA UTARA"
      },
      {
        value: "DKI JAKARTA",
        color: "#066357",
        label: "DKI JAKARTA"
      },
      {
        value: "KEPULAUAN RIAU",
        color: "#077F6F",
        label: "KEPULAUAN RIAU"
      },
      {
        value: "SULAWESI TENGGARA",
        color: "#088C7A",
        label: "SULAWESI TENGGARA"
      },
      {
        value: "LAMPUNG",
        color: "#39A395",
        label: "LAMPUNG"
      },
      {
        value: "KEPULAUAN BANGKA BELITUNG",
        color: "#5AB2A6",
        label: "KEPULAUAN BANGKA BELITUNG"
      },
      {
        value: "SUMATERA BARAT",
        color: "#8DCAC2",
        label: "SUMATERA BARAT"
      },
      {
        value: "DAERAH ISTIMEWA YOGYAKARTA",
        color: "#B2DBD6",
        label: "DAERAH ISTIMEWA YOGYAKARTA"
      },
      {
        value: "JAWA TIMUR",
        color: "#E6F4F2",
        label: "JAWA TIMUR"
      }
    ]
  },
  categories: [
    {
      attribute_name: "id",
      created_at: "2025-01-30T09:28:23.831Z",
      data_type: DataSchemaType.INT,
      id: "21735252-9b5b-42c8-b244-9e39ceec5f1e",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: false,
      is_spatial: false,
      position: 1,
      updated_at: "2025-01-30T09:28:23.831Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "nama",
      created_at: "2025-01-30T09:28:23.832Z",
      data_type: DataSchemaType.TEXT,
      id: "9ff554cf-0b90-4c98-805a-e80d27387d2a",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: false,
      is_spatial: false,
      position: 2,
      updated_at: "2025-01-30T09:28:23.832Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "ibukota",
      created_at: "2025-01-30T09:28:23.83Z",
      data_type: DataSchemaType.TEXT,
      id: "f1145628-38e8-4f9a-b507-e1524769b136",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: true,
      is_spatial: false,
      position: 3,
      updated_at: "2025-04-21T09:42:00.437679Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "penduduk",
      created_at: "2025-01-30T09:38:44.381Z",
      data_type: DataSchemaType.INT,
      id: "a5e850bd-08fa-4043-9a2a-29cc073d1da4",
      is_nullable: false,
      is_reference_geo: false,
      is_reference_id: false,
      is_spatial: false,
      position: 7,
      updated_at: "2025-01-30T09:38:44.381Z",
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      attribute_name: "geom",
      created_at: "2025-01-30T09:38:44.381Z",
      data_type: DataSchemaType.GEOMETRY,
      id: "2d6c2592-2c3d-4e64-b35b-2b565c080981",
      is_nullable: false,
      is_reference_geo: true,
      is_reference_id: false,
      is_spatial: true,
      position: 8,
      updated_at: "2025-04-21T03:03:35.471752Z",
      is_visible: true,
      paired_key_attribute_id: null
    }
  ],
  selectedCategory: {
    attribute_name: "nama",
    created_at: "2025-01-30T09:28:23.832Z",
    data_type: DataSchemaType.TEXT,
    id: "9ff554cf-0b90-4c98-805a-e80d27387d2a",
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: 2,
    updated_at: "2025-01-30T09:28:23.832Z",
    is_visible: true,
    paired_key_attribute_id: null
  }
};

export const populationByProvinceRawData: DatasetData = {
  columns: [
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_INTEGER,
      name: "id",
      code: "id"
    }),
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_STRING,
      name: "nama",
      code: "nama"
    }),
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_STRING,
      name: "ibukota",
      code: "ibukota"
    }),
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_INTEGER,
      name: "penduduk",
      code: "penduduk"
    }),
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_GEOMETRY,
      name: "geom",
      code: "geom"
    })
  ],
  dataset_id: "1db1e893-f6a0-4331-919a-20fd9901d71c",
  name: "population by province",
  rows: [
    [1, "ACEH", "Banda Aceh", 5325010, ""],
    [2, "SUMATERA UTARA", "Medan", 15180796, ""],
    [3, "DKI JAKARTA", "Jakarta", 11246067, ""]
  ],
  spatial_aggregation_type: "polygon",
  dataset_size_kb: 100
};

export const populationByProvinceRawDataV2: DatasetData = {
  columns: [
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_INTEGER,
      name: "id",
      code: "id"
    }),
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_STRING,
      name: "nama",
      code: "nama"
    }),
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_STRING,
      name: "ibukota",
      code: "ibukota"
    }),
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_INTEGER,
      name: "penduduk",
      code: "penduduk"
    }),
    createMockDatasetDataColumn({
      data_type: DatasetDataType.TYPE_GEOMETRY,
      name: "geom",
      code: "geom"
    })
  ],
  dataset_id: "1db1e893-f6a0-4331-919a-20fd9901d71c",
  name: "population by province",
  rows: [
    [1, "ACEH", "Banda Aceh", 5325010, ""],
    [2, "SUMATERA UTARA", "Medan", 15180796, ""],
    [3, "DKI JAKARTA", "Jakarta", 11246067, ""]
  ],
  spatial_aggregation_type: "polygon",
  dataset_size_kb: 100
};

export const populationByProvinceSelectedDataset: Dataset = {
  data: {
    id: "3cdc5bd9-135a-4e7b-8895-4976aaa80dfe",
    name: "population by province",
    spatial_aggregation_type: "polygon",
    country_id: "id",
    source: "bvt",
    short_description: "short description",
    published: true,
    created_at: "2021-01-01",
    updated_at: "2021-01-01",
    available: true,
    total_rows: 3,
    schema: [],
    dataset_size_kb: 100
  },
  properties: {
    isVisible: false,
    spatialType: "polygon"
  },
  style: {
    stroke: {
      attribute_id: "1",
      code: "Name",
      attribute_name: "Name",
      opacity: 1,
      width: 1
    },
    weight: 1,
    opacity: 1,
    color: "#fff",
    fillColor: "#088C7A",
    colorFamily: EColorNames.TEAL,
    fillOpacity: 0.7,
    visibility: "none",
    colorRanges: [
      {
        value: "DKI JAKARTA",
        color: "#033B33",
        label: "DKI JAKARTA"
      }
    ],
    labelConfig: {
      attribute_id: "1",
      code: "Name",
      attribute_name: "Name",
      show_label: true,
      color_scale: EColorLabel.BLACK_WHITE,
      opacity: 0.7
    },
    rankConfig: {
      isEnabled: false,
      maxRanking: 1
    },
    colorSteps: 10
  },
  categories: [
    {
      id: "0",
      code: "catchment_id",
      attribute_name: "catchment_id",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 0,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "1",
      code: "id",
      attribute_name: "id",
      data_type: DataSchemaType.INT,
      is_nullable: false,
      is_spatial: false,
      position: 1,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "2",
      code: "nama",
      attribute_name: "nama",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 2,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "3",
      code: "ibukota",
      attribute_name: "ibukota",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 3,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "4",
      code: "geom",
      attribute_name: "geom",
      data_type: DataSchemaType.GEOMETRY,
      is_nullable: false,
      is_spatial: false,
      position: 4,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "5",
      code: "percentage_intersect_catchment",
      attribute_name: "percentage_intersect_catchment",
      data_type: DataSchemaType.FLOAT,
      is_nullable: false,
      is_spatial: false,
      position: 5,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "6",
      code: "percentage_intersect_area",
      attribute_name: "percentage_intersect_area",
      data_type: DataSchemaType.FLOAT,
      is_nullable: false,
      is_spatial: false,
      position: 6,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "7",
      code: "penduduk_as_is",
      attribute_name: "penduduk_as_is",
      data_type: DataSchemaType.INT,
      is_nullable: false,
      is_spatial: false,
      position: 7,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "8",
      code: "penduduk_proportion",
      attribute_name: "penduduk_proportion",
      data_type: DataSchemaType.FLOAT,
      is_nullable: false,
      is_spatial: false,
      position: 8,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    }
  ],
  selectedCategory: {
    id: "2",
    code: "nama",
    attribute_name: "nama",
    data_type: DataSchemaType.TEXT,
    is_nullable: false,
    is_spatial: false,
    position: 2,
    created_at: "",
    updated_at: "",
    is_reference_geo: false,
    is_reference_id: false,
    is_visible: true,
    paired_key_attribute_id: null
  }
};

export const featureCollectionPointPolygon: FeatureCollection<Geometry> = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      id: "1-0",
      geometry: {
        type: "Polygon",
        coordinates: [[[107.24817441785171, -6.537172399615905]]]
      },
      properties: {
        catchment_id: "1",
        location_id: "A1",
        location_name: "marker-0",
        score: 984.4780163649433,
        penduduk_proportion: 984.4780163649433,
        ["Geometry Source"]: "catchment_geometry"
      }
    },
    {
      type: "Feature",
      id: "3-1",
      geometry: {
        type: "Point",
        coordinates: [107.24817441785171, -6.541669001434528]
      },
      properties: {
        catchment_id: "1",
        location_id: "A1",
        location_name: "marker-0",
        score: 984.4780163649433,
        penduduk_proportion: 984.4780163649433,
        ["Geometry Source"]: "location_geometry"
      }
    }
  ]
};

export const siteProfilingDataset: Dataset = {
  data: {
    id: "a14ff193-ce3b-4d28-9f07-602cce9339a3",
    name: "spatial analysis result",
    spatial_aggregation_type: EOutputAnalysisType.PROFILING,
    country_id: "id",
    source: "bvt",
    short_description: "short description",
    published: true,
    created_at: "2021-01-01",
    updated_at: "2021-01-01",
    available: true,
    total_rows: 1,
    schema: []
  },
  properties: {
    isVisible: true,
    spatialType: EOutputAnalysisType.PROFILING
  },
  style: {
    stroke: {
      attribute_id: "1",
      code: "Name",
      attribute_name: "Name",
      opacity: 1,
      width: 1
    },
    weight: 1,
    opacity: 1,
    color: "#fff",
    fillColor: "#088C7A",
    colorFamily: EColorNames.TEAL,
    fillOpacity: 0.7,
    visibility: "visible",
    colorRanges: [],
    labelConfig: {
      attribute_id: "1",
      code: "Name",
      attribute_name: "Name",
      show_label: true,
      color_scale: EColorLabel.BLACK_WHITE,
      opacity: 0.7
    },
    colorSteps: 10
  },
  categories: [
    {
      id: "0",
      code: "catchment_id",
      attribute_name: "catchment_id",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 0,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "1",
      code: "catchment_geometry",
      attribute_name: "catchment_geometry",
      data_type: DataSchemaType.GEOMETRY,
      is_nullable: false,
      is_spatial: false,
      position: 1,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "2",
      code: "location_id",
      attribute_name: "location_id",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 2,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "3",
      code: "location_geometry",
      attribute_name: "location_geometry",
      data_type: DataSchemaType.GEOMETRY,
      is_nullable: false,
      is_spatial: false,
      position: 3,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "4",
      code: "location_name",
      attribute_name: "location_name",
      data_type: DataSchemaType.TEXT,
      is_nullable: false,
      is_spatial: false,
      position: 4,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "5",
      code: "score",
      attribute_name: "score",
      data_type: DataSchemaType.FLOAT,
      is_nullable: false,
      is_spatial: false,
      position: 5,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    },
    {
      id: "6",
      code: "penduduk_proportion",
      attribute_name: "penduduk_proportion",
      data_type: DataSchemaType.FLOAT,
      is_nullable: false,
      is_spatial: false,
      position: 6,
      created_at: "",
      updated_at: "",
      is_reference_geo: false,
      is_reference_id: false,
      is_visible: true,
      paired_key_attribute_id: null
    }
  ],
  selectedCategory: {
    id: "2",
    code: "location_id",
    attribute_name: "location_id",
    data_type: DataSchemaType.TEXT,
    is_nullable: false,
    is_spatial: false,
    position: 2,
    created_at: "",
    updated_at: "",
    is_reference_geo: false,
    is_reference_id: false,
    is_visible: true,
    paired_key_attribute_id: null
  }
};

export const mockDatasetData = {
  columns: [
    {
      data_type: "TYPE_FLOAT",
      name: "inflation_rate"
    },
    {
      data_type: "TYPE_GEOMETRY",
      name: "adm_area_geometry"
    },
    {
      data_type: "TYPE_STRING",
      name: "province_name"
    },
    {
      data_type: "TYPE_STRING",
      name: "province_code"
    }
  ],
  dataset_id: "4f081178-3102-4ad8-a8da-b8a4545f50d3",
  name: "Thematic Province Inflation 2018",
  rows: [
    [
      3.27,
      "MULTIPOLYGON(((106.7261 -6.0895,106.7261 -6.0896,106.7264 -6.09,106.7266 -6.09,106.7269 -6.0896,106.727)))",
      "DKI JAKARTA",
      "31"
    ]
  ],
  spatial_aggregation_type: "adm_area"
};

export const mockDatasetMultiGeomSchema = {
  point: {
    schemas: [
      {
        attribute_name: "location_point_wkt",
        created_at: "2025-06-11T13:49:41.364046Z",
        data_type: "geometry",
        id: "location_point_wkt",
        is_nullable: false,
        is_reference_geo: false,
        is_reference_id: false,
        is_spatial: true,
        position: 7,
        updated_at: "2025-06-11T13:49:41.364046Z",
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        attribute_name: "location_point_wkt2",
        created_at: "2025-06-11T13:49:41.370515Z",
        data_type: "geometry",
        id: "location_point_wkt2",
        is_nullable: false,
        is_reference_geo: false,
        is_reference_id: false,
        is_spatial: true,
        position: 9,
        updated_at: "2025-06-11T13:49:41.370515Z",
        is_visible: true,
        paired_key_attribute_id: null
      }
    ]
  },
  polygon: {
    schemas: [
      {
        attribute_name: "polygon_wkt2",
        created_at: "2025-06-11T13:49:41.36699Z",
        data_type: "geometry",
        id: "polygon_wkt2",
        is_nullable: false,
        is_reference_geo: false,
        is_reference_id: false,
        is_spatial: true,
        position: 8,
        updated_at: "2025-06-11T13:49:41.36699Z",
        is_visible: true,
        paired_key_attribute_id: null
      },
      {
        attribute_name: "polygon_wkt",
        created_at: "2025-06-11T13:49:41.360946Z",
        data_type: "geometry",
        id: "polygon_wkt",
        is_nullable: false,
        is_reference_geo: false,
        is_reference_id: false,
        is_spatial: true,
        position: 6,
        updated_at: "2025-06-11T13:49:41.360946Z",
        is_visible: true,
        paired_key_attribute_id: null
      }
    ]
  }
};

export const defaultDataset: Dataset = {
  data: {
    schema: [],
    id: "4f081178-3102-4ad8-a8da-b8a4545f50d3",
    name: "",
    spatial_aggregation_type: "adm_area",
    country_id: "id",
    source: "bvt",
    short_description: "short description",
    published: true,
    created_at: "2021-01-01",
    updated_at: "2021-01-01",
    available: true,
    total_rows: 100
  },
  properties: {
    isVisible: true,
    spatialType: "adm_area"
  },
  style: {
    stroke: {
      attribute_id: "1",
      code: "Name",
      attribute_name: "Name",
      opacity: 1,
      width: 1
    },
    weight: 1,
    opacity: 1,
    color: "#fff",
    fillColor: COLORS.TEAL[500],
    colorFamily: EColorNames.TEAL,
    fillOpacity: 0.7,
    visibility: "visible",
    colorRanges: [],
    labelConfig: {
      attribute_id: "1",
      code: "Name",
      attribute_name: "Name",
      show_label: false,
      color_scale: EColorLabel.BLACK_WHITE,
      opacity: 0.7
    },
    rankConfig: {
      isEnabled: false,
      maxRanking: 10
    },
    colorSteps: 10
  },
  categories: [],
  selectedCategory: {
    id: "",
    attribute_name: "",
    data_type: DataSchemaType.TEXT,
    is_nullable: false,
    is_spatial: false,
    position: 0,
    created_at: "",
    updated_at: "",
    is_reference_geo: false,
    is_reference_id: false,
    is_visible: true,
    paired_key_attribute_id: null
  }
};

export const mockPostDatasetData = {
  columns: [],
  rows: new Array(10).fill([]),
  dataset_id: "",
  name: "",
  display_name: "",
  spatial_aggregation_type: "poi",
  dataset_size_kb: 100
};

export const mockGetAttributeValuesByAttributeId = [
  {
    display_name: "Klinik Taman Sari",
    value: "1"
  },
  {
    display_name: "Klinik Piso Medika Ruko Grand Centro",
    value: "2"
  }
];

export const mockSpatialAggregationTypes: SpatialAggregationTypeItem[] = [
  {
    code: "adm_area",
    name: "Adm. Area"
  },
  {
    code: "geohash",
    name: "Geohash"
  },
  {
    code: "H3",
    name: "H3"
  },
  {
    code: "poi",
    name: "POI"
  },
  {
    code: "polygon",
    name: "Polygon"
  }
];

export const mockDataSourceProviders = [
  {
    id: "4920bee2-38dd-48c3-ac5f-52ae00a27958",
    logo_asset_url:
      "https://tse2.mm.bing.net/th/id/OIP.tIA5TpSDs6AFE8sZQwzlRQHaHa?rs=1\u0026pid=ImgDetMain\u0026o=7\u0026rm=3",
    name: "BVT"
  },
  {
    id: "9fe105ed-1ae6-4d02-b131-55a5a990a7db",
    logo_asset_url: "https://picsum.dev/300/200",
    name: "Visa"
  },
  {
    id: "0397a78b-c8ca-4f1c-a358-0e9b50a74e77",
    logo_asset_url:
      "https://tse4.mm.bing.net/th/id/OIP.R_u30W-TjSHFG2FNEcstPgHaGv?rs=1\u0026pid=ImgDetMain\u0026o=7\u0026rm=3",
    name: "here"
  },
  {
    id: "8231c256-30a0-44e4-b7f8-4da1325f11a3",
    logo_asset_url: "https://picsum.dev/300/200",
    name: "Mapbox"
  }
];

export const mockDeleteRowsResponse = {
  deleted_rows: 2,
  total_rows: 90,
  dataset_id: "dataset-1",
  dataset_name: "Sample Dataset",
  dataset_size_kb: 2048,
  successful_deletes: ["1", "2"]
};

export const mockAddUpdateRowsResponse = {
  columns: [
    { data_type: DatasetDataType.TYPE_STRING, name: "id", code: "id" },
    { data_type: DatasetDataType.TYPE_STRING, name: "name", code: "name" }
  ],
  rows: [["1", "Location A"]],
  dataset_id: "dataset-1",
  name: "Sample Dataset",
  spatial_aggregation_type: "poi",
  dataset_size_kb: 2050
};

export const mockDatasetStatus = {
  row_count: 0,
  count_status: DatasetStatsCountStatusENUM.EXACT
};

export const mockDatasetConnectors: DatasetConnectorItem[] = [
  {
    connector_code: EDatasetConnector.LI,
    connector_name: "Upload file",
    description: "Upload a supported spatial or tabular file.",
    icon_name: "upload",
    is_active: true,
    is_connected: false,
    status: EDatasetConnectorStatus.AVAILABLE,
    updated_at: null
  },
  {
    connector_code: EDatasetConnector.TARGETIN,
    connector_name: "Targetin",
    description: "Import field-survey datasets and responses.",
    icon_name: "targetin",
    is_active: false,
    is_connected: true,
    status: EDatasetConnectorStatus.AVAILABLE,
    updated_at: null
  }
];
